import styled from "styled-components";
import type {
  WorkPolicyApplicationRecovery,
  WorkPolicyApplicationUiStatus
} from "./use-work-policy-application-controller";

interface WorkPolicySaveRecoveryProps {
  status: WorkPolicyApplicationUiStatus;
  approvalRequestIdx?: number;
  recovery: WorkPolicyApplicationRecovery | null;
  onRetry?: () => Promise<void>;
}

const State = styled.section`
  padding: 12px;
  border: 1px solid ${props => props.theme.colors.grey};
`;

const stageLabel = {
  NONE: "저장 시작 전",
  MASTER_SAVED: "문서 기본정보 저장 완료",
  POLICY_BOUND: "근무제 정책 연결 완료",
  TYPE_DETAIL_SAVED: "근무 일정 저장 완료",
  PARTICIPANTS_SAVED: "결재 참여자 저장 완료",
  FILES_SAVED: "첨부파일 저장 완료",
  RECONCILED: "최종 확인 완료"
} as const;

const recoveryMessageByCode: Record<string, string> = {
  WORK_POLICY_STAGE_FAILED:
    "저장 중 문제가 발생했습니다. 입력값은 유지되었습니다.",
  WORK_POLICY_STALE:
    "다른 변경과 충돌했습니다. 문서를 다시 불러와 확인해 주세요.",
  WORK_POLICY_DB_DEPENDENCY:
    "서버 연결 상태를 확인하고 잠시 후 다시 시도해 주세요.",
  WORK_POLICY_VALIDATION: "신청 내용을 확인해 주세요.",
  WORK_POLICY_FORBIDDEN: "이 문서를 변경할 권한이 없습니다.",
  WORK_POLICY_NOT_FOUND: "신청 문서를 찾지 못했습니다.",
  WORK_POLICY_NETWORK_UNKNOWN: "응답을 확인하지 못했습니다."
};

function userFacingRecoveryMessage(recovery: WorkPolicyApplicationRecovery) {
  const knownMessage = recoveryMessageByCode[recovery.code];
  if (knownMessage) return knownMessage;
  return /[가-힣]/.test(recovery.message)
    ? recovery.message
    : "요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.";
}

export function WorkPolicySaveRecovery({
  status,
  approvalRequestIdx,
  recovery,
  onRetry
}: WorkPolicySaveRecoveryProps) {
  if (status === "idle") return null;
  if (status === "saving") {
    return (
      <State role="status">
        저장 중입니다. 중복 요청을 막기 위해 완료 전까지 기다려 주세요.
        {approvalRequestIdx ? ` 요청 ID ${approvalRequestIdx}` : ""}
      </State>
    );
  }
  if (status === "draft" || status === "submitted") {
    return (
      <State role="status">
        {status === "draft" ? "임시저장됨" : "상신 완료"}
        {approvalRequestIdx ? ` · 요청 ID ${approvalRequestIdx}` : ""}
      </State>
    );
  }
  if (status === "unsupported-historical") return null;
  if (!recovery) return <State role="alert">저장 상태를 확인하지 못했습니다.</State>;
  return (
    <State role="alert">
      <strong>
        {status === "unsupported-policy"
          ? "지원하지 않는 근무제"
          : recovery.networkUnknown
          ? "네트워크 결과 불명확"
          : "저장을 완료하지 못함"}
      </strong>
      <p>{userFacingRecoveryMessage(recovery)}</p>
      {approvalRequestIdx && <p>요청 ID {approvalRequestIdx}</p>}
      {recovery.lastCompletedStage && (
        <p>마지막 완료 상태: {stageLabel[recovery.lastCompletedStage]}</p>
      )}
      {recovery.networkUnknown && (
        <p>중복 생성을 막기 위해 서버 상태를 먼저 확인합니다.</p>
      )}
      {recovery.retryable && approvalRequestIdx && onRetry && (
        <button type="button" onClick={() => void onRetry()}>
          같은 요청으로 재시도
        </button>
      )}
    </State>
  );
}
